import AcUnitIcon from "@mui/icons-material/AcUnit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ContactsIcon from "@mui/icons-material/Contacts";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorageIcon from "@mui/icons-material/Storage";
import TimelineIcon from "@mui/icons-material/Timeline";
import WorkIcon from "@mui/icons-material/Work";
import {
	Badge,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Link,
	Paper,
	Stack,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { ReactNode, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PersonPhonenumber, Person as PersonType } from "../../api";
import { LinkedinJobPosting } from "../../api/job/linkedin_job_posting";
import {
	dataminePerson,
	deletePerson,
	extractPerson,
	patchPerson,
} from "../../api/person/person";
import { PersonEmail } from "../../api/personemail/person-email";
import BirthdaySELogo from "../../assets/images/birthday4.se.svg?react";
import EniroLogo from "../../assets/images/Eniro-Logo.wine.svg?react";
import HittaSELogo from "../../assets/images/hitta.svg?react";
import RatsitLogo from "../../assets/images/ratsit-logo-white.svg?react";
import { useFitTabLabels } from "../../hooks/use-fit-tab-labels";
import { formatDateTime } from "../../utils/date-formatting";
import { Cooldown } from "../cooldown";
import LinkTab from "../link-tab";
import { ReminderView } from "../reminder/reminder-view";
import { TagView } from "../tag_history";
import { BadgeAmountContext } from "./badge-amount-context";
import { PersonActivityTimeline } from "./communication-timeline";
import PersonEmailTable from "./PersonEmailTable";
import PersonJobPosting from "./person_jobposting";
import { PersonAssignments } from "./person-assignments";
import PersonContactInfoStatus from "./person-contactinfo-status";
import PersonDataSourcesStatus from "./person-data-sources-status";
import { PersonGetProfilesQueue } from "./person-get-profiles-queue";
import { PersonGradeConnectQueue } from "./person-grade-connect-queue";
import { PersonGroups } from "./person-groups";
import PersonInmailCostStatus from "./person-inmail-cost-status";
import { PersonPeopleAlsoViewed } from "./person-people-also-viewed";
import PersonPhoneNumbers from "./person-phonenumbers";
import { ProfileBinaryHistory } from "./person-profile-binary";
import { PersonSendInviteQueue } from "./person-send-invite-queue";

import { PersonVoyagerLogHistory } from "./person-voyager-log";

interface TabPanelProps {
	children?: ReactNode;
	index: number;
	value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
	return (
		<Box
			role="tabpanel"
			hidden={value !== index}
			data-component={`tab-panel-${index}`}
			sx={{ p: 2 }}
		>
			{value === index && children}
		</Box>
	);
}

interface PersonActionsTabsProps {
	personId: number;
	personInfo?: PersonType | null;
	assignmentId?: number;
	isSuperuser: boolean;
	jobPostings: LinkedinJobPosting[];
	personEmails?: PersonEmail[];
	personPhoneNumbers?: PersonPhonenumber[];
	activeTab?: string;
	onTabChange?: (tabSlug: string) => void;
	buildTabHref?: (tabSlug: string) => string;
	/** Incremented externally to trigger PersonAssignments refetch */
	roleAddedCounter?: number;
	/** Pre-seed Activity tab filters (e.g. ["notes"] for COMMENT chip click). */
	initialActivityFilters?: import("./communication-timeline/timeline-types").ActivityFilter[];
}

// Convert label to URL slug
const labelToSlug = (label: string): string => label.toLowerCase().replace(/\s+/g, "-");

export const PersonActionsTabs = ({
	personId,
	personInfo,
	assignmentId,
	isSuperuser,
	jobPostings,
	personEmails = [],
	personPhoneNumbers = [],
	activeTab: activeTabSlug,
	onTabChange,
	buildTabHref,
	roleAddedCounter,
	initialActivityFilters,
}: PersonActionsTabsProps) => {
	const [isExtracting, setIsExtracting] = useState(false);
	const [extractError, setExtractError] = useState<string | null>(null);
	const [isDatamining, setIsDatamining] = useState(false);
	const [datamineError, setDatamineError] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editName, setEditName] = useState("");
	const [editVanityName, setEditVanityName] = useState("");
	const [isEditing, setIsEditing] = useState(false);
	const [editError, setEditError] = useState<string | null>(null);
	const { badgeAmount } = useContext(BadgeAmountContext);
	const theme = useTheme();
	const navigate = useNavigate();

	const handleExtract = async () => {
		setIsExtracting(true);
		setExtractError(null);
		try {
			await extractPerson(personId);
			window.location.reload();
		} catch (error) {
			console.error("Error extracting person:", error);
			setExtractError(
				error instanceof Error ? error.message : "Failed to extract person data"
			);
		} finally {
			setIsExtracting(false);
		}
	};

	const handleDatamine = async () => {
		setIsDatamining(true);
		setDatamineError(null);
		try {
			await dataminePerson(personId);
			window.location.reload();
		} catch (error) {
			console.error("Error datamining person:", error);
			setDatamineError(
				error instanceof Error ? error.message : "Failed to datamine person"
			);
		} finally {
			setIsDatamining(false);
		}
	};

	const handleDelete = async () => {
		setIsDeleting(true);
		setDeleteError(null);
		setDeleteDialogOpen(false);
		try {
			await deletePerson(personId);
			navigate("/");
		} catch (error) {
			console.error("Error deleting person:", error);
			setDeleteError(
				error instanceof Error ? error.message : "Failed to delete person"
			);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleOpenEditDialog = () => {
		setEditName(personInfo?.name ?? "");
		setEditVanityName(personInfo?.vanity_name ?? "");
		setEditError(null);
		setEditDialogOpen(true);
	};

	const handleSaveEdit = async () => {
		setIsEditing(true);
		setEditError(null);
		try {
			await patchPerson(personId, {
				name: editName,
				vanity_name: editVanityName || null,
			});
			setEditDialogOpen(false);
			window.location.reload();
		} catch (error) {
			console.error("Error editing person:", error);
			setEditError(
				error instanceof Error ? error.message : "Failed to update person"
			);
		} finally {
			setIsEditing(false);
		}
	};

	const tabs = [
		{
			label: "Activity",
			icon: <TimelineIcon />,
			badge: 0,
			content: (
				<PersonActivityTimeline
					personId={personId}
					personEmails={personEmails}
					initialFilters={initialActivityFilters}
				/>
			),
		},
		{
			label: "Contact",
			icon: <ContactsIcon />,
			content: (
				<Stack spacing={2} data-component="contact-info-stack">
					<PersonPhoneNumbers
						personId={personId}
						personPhoneNumber={personPhoneNumbers}
						emailsEditable={true}
					/>
					<PersonEmailTable
						personId={personId}
						personEmails={personEmails}
						emailsEditable={true}
						compact={!isSuperuser}
					/>
				</Stack>
			),
		},
		{
			label: "Tags",
			icon: <LocalOfferIcon />,
			badge: 0,
			content: (
				<TagView
					objectName="persons"
					objectId={personId}
					showDebug={isSuperuser}
				/>
			),
		},
		{
			label: "Reminders",
			icon: <NotificationsIcon />,
			badge: badgeAmount.reminders,
			content: <ReminderView objectName="persons" objectId={personId} />,
		},
		{
			label: "Roles",
			icon: <AssignmentIcon />,
			badge: 0,
			content: (
				<PersonAssignments
					personId={personId}
					roleAddedCounter={roleAddedCounter}
				/>
			),
		},
		{
			label: "Cooldown",
			icon: <AcUnitIcon />,
			badge: 0,
			content: <Cooldown personId={personId} />,
		},
		...(jobPostings.length > 0
			? [
					{
						label: "Jobs",
						icon: <WorkIcon />,
						content: (
							<PersonJobPosting jobPostings={jobPostings} personPage />
						),
					},
				]
			: []),
		...(isSuperuser
			? [
					{
						label: "Data",
						icon: <StorageIcon />,
						badge: 0,
						content: (
							<Stack spacing={2} data-component="data-stack">
								{/* Profile content (when personInfo available) */}
								{personInfo && (
									<>
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												gap: 0.5,
												p: 1,
												backgroundColor: "action.hover",
												borderRadius: 1,
											}}
											data-component="superuser-metadata-box"
										>
											<Typography
												variant="caption"
												data-component="metadata-person-id"
											>
												Person ID: {personId}
											</Typography>
											<Typography
												variant="caption"
												data-component="metadata-created"
											>
												Created:{" "}
												{formatDateTime(personInfo.created)}
											</Typography>
											<Typography
												variant="caption"
												data-component="metadata-updated"
											>
												Updated:{" "}
												{formatDateTime(personInfo.updated)}
											</Typography>
											<Typography
												variant="caption"
												data-component="metadata-extracted"
											>
												Extracted:{" "}
												{formatDateTime(
													personInfo.last_extracted
												)}
											</Typography>
											<Typography
												variant="caption"
												data-component="metadata-vanity-name"
											>
												Vanity Name:{" "}
												{personInfo.vanity_name ? (
													<Link
														href={`https://www.linkedin.com/in/${personInfo.vanity_name}/`}
														target="_blank"
														rel="noopener noreferrer"
													>
														{personInfo.vanity_name}
													</Link>
												) : (
													"Not set"
												)}
											</Typography>
											<Typography
												variant="caption"
												data-component="metadata-user-hash"
											>
												User Hash:{" "}
												<Link
													href={`https://www.linkedin.com/in/${personInfo.user_hash}/`}
													target="_blank"
													rel="noopener noreferrer"
												>
													{personInfo.user_hash}
												</Link>
											</Typography>
										</Box>
										<Stack
											direction="row"
											spacing={1}
											flexWrap="wrap"
										>
											<Button
												data-component="cv-text-button"
												size="small"
												variant="outlined"
												startIcon={<DescriptionIcon />}
												href={`/person/${personId}/text`}
											>
												CV Text
											</Button>
											<Button
												data-component="edit-button"
												size="small"
												variant="outlined"
												startIcon={<EditIcon />}
												onClick={handleOpenEditDialog}
												title="Edit person name and vanity name"
											>
												Edit
											</Button>
											<Button
												data-component="extract-button"
												size="small"
												variant="outlined"
												startIcon={
													isExtracting ? (
														<CircularProgress size={16} />
													) : (
														<RefreshIcon />
													)
												}
												onClick={handleExtract}
												disabled={isExtracting}
												title="Extract person data from latest ProfileFetchBatch"
											>
												Extract
											</Button>
											<Button
												data-component="datamine-button"
												size="small"
												variant="outlined"
												startIcon={
													isDatamining ? (
														<CircularProgress size={16} />
													) : (
														<CloudDownloadIcon />
													)
												}
												onClick={handleDatamine}
												disabled={isDatamining}
												title="Datamine person profile from LinkedIn (fetches fresh data)"
											>
												Datamine
											</Button>
											<Button
												data-component="delete-button"
												size="small"
												variant="outlined"
												color="error"
												startIcon={
													isDeleting ? (
														<CircularProgress size={16} />
													) : (
														<DeleteIcon />
													)
												}
												onClick={() =>
													setDeleteDialogOpen(true)
												}
												disabled={isDeleting}
												title="Delete this person from the database"
											>
												Delete
											</Button>
										</Stack>
										{extractError && (
											<Typography variant="caption" color="error">
												{extractError}
											</Typography>
										)}
										{datamineError && (
											<Typography variant="caption" color="error">
												{datamineError}
											</Typography>
										)}
										{deleteError && (
											<Typography variant="caption" color="error">
												{deleteError}
											</Typography>
										)}
										{editError && (
											<Typography variant="caption" color="error">
												{editError}
											</Typography>
										)}
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Lookup Links
										</Typography>
										<Stack
											direction="row"
											spacing={0.5}
											flexWrap="wrap"
											data-component="lookup-links"
										>
											<IconButton
												target="_blank"
												size="small"
												component={Link}
												href={`https://www.ratsit.se/sok/person?vem=${personInfo.name}&m=0&k=0&r=0&er=0&b=0&eb=0&amin=16&amax=120&fon=1&page=1`}
												data-component="ratsit-link"
											>
												<RatsitLogo
													style={{
														fill: theme.palette.text
															.secondary,
														height: "24px",
													}}
												/>
											</IconButton>
											<IconButton
												target="_blank"
												size="small"
												component={Link}
												href={`https://www.birthday.se/sok?who=${personInfo.name}&lk=01&match=on`}
												data-component="birthday-link"
											>
												<BirthdaySELogo
													style={{
														height: "24px",
													}}
												/>
											</IconButton>
											<IconButton
												target="_blank"
												size="small"
												component={Link}
												href={`https://www.hitta.se/s%C3%B6k?vad=${personInfo.name}`}
												data-component="hitta-link"
											>
												<HittaSELogo
													style={{
														height: "44px",
														width: "44px",
													}}
												/>
											</IconButton>
											<IconButton
												target="_blank"
												size="small"
												component={Link}
												href={`https://www.eniro.se/${personInfo.name}/personer`}
												data-component="eniro-link"
											>
												<EniroLogo
													style={{
														fill: theme.palette.text
															.secondary,
														height: "44px",
														width: "120px",
													}}
												/>
											</IconButton>
											<IconButton
												target="_blank"
												size="small"
												component={Link}
												href={`https://www.merinfo.se/search?q=${personInfo.name}`}
												data-component="mininfo-link"
											>
												MININFO
											</IconButton>
											<IconButton
												target="_blank"
												size="small"
												component={Link}
												href={`https://mrkoll.se/resultat?n==${personInfo.name}&min=16&max=120`}
												data-component="mrkoll-link"
											>
												mrkoll
											</IconButton>
											<IconButton
												target="_blank"
												size="small"
												component={Link}
												href={`https://rocketreach.co/person?start=1&pageSize=10&link=${personInfo.url}`}
												data-component="rocketreach-link"
											>
												rocketreach
											</IconButton>
										</Stack>
									</>
								)}
								{/* Data sources content */}
								<PersonDataSourcesStatus personId={personId} />
								<PersonInmailCostStatus personId={personId} />
								<PersonContactInfoStatus personId={personId} />
								<PersonPeopleAlsoViewed personId={personId} />
								<PersonPeopleAlsoViewed
									personId={personId}
									direction="incoming"
								/>
								<PersonGroups personId={personId} />
								<ProfileBinaryHistory personId={personId} />
								<PersonGradeConnectQueue personId={personId} />
								<PersonGetProfilesQueue personId={personId} />
								<PersonSendInviteQueue personId={personId} />
								<PersonVoyagerLogHistory personId={personId} />
							</Stack>
						),
					},
				]
			: []),
	];

	// Show text labels only while the tab bar is wide enough, else icons only.
	const { ref: tabsFitRef, showLabels } = useFitTabLabels(tabs.length);

	// Local state for uncontrolled mode (when no activeTab/onTabChange provided)
	const [localTabIndex, setLocalTabIndex] = useState(0);

	// Calculate active tab index from URL slug (controlled mode) or use local state
	const controlledTabIndex = activeTabSlug
		? tabs.findIndex((tab) => labelToSlug(tab.label) === activeTabSlug)
		: -1;
	const currentTabIndex =
		controlledTabIndex >= 0 ? controlledTabIndex : localTabIndex;

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		const tab = tabs[newValue];
		if (tab && onTabChange) {
			// Controlled mode - notify parent
			onTabChange(labelToSlug(tab.label));
		} else {
			// Uncontrolled mode - manage locally
			setLocalTabIndex(newValue);
		}
	};

	return (
		<Paper
			data-component="person-actions-tabs"
			sx={{ height: "100%", display: "flex", flexDirection: "column" }}
		>
			<Tabs
				ref={tabsFitRef}
				value={currentTabIndex}
				onChange={handleTabChange}
				variant="scrollable"
				scrollButtons={false}
				data-component="actions-tabs-header"
				sx={{
					flexShrink: 0,
					borderBottom: 1,
					borderColor: "divider",
					minHeight: 36,
					"& .MuiTab-root": {
						minWidth: "auto",
						minHeight: 36,
						px: 1,
						py: 0.5,
						fontSize: "0.75rem",
					},
				}}
			>
				{tabs.map((tab, index) => {
					const tabIcon =
						tab.badge > 0 ? (
							<Badge badgeContent={tab.badge} color="primary">
								{tab.icon}
							</Badge>
						) : (
							tab.icon
						);

					const slug = labelToSlug(tab.label);
					// When labels don't fit, show icons only with the label as a hover
					// tooltip. The Tooltip wraps the icon (not the Tab) so the Tab stays a
					// direct child of Tabs and the selected-indicator keeps working.
					const commonProps = {
						icon: showLabels ? (
							tabIcon
						) : (
							<Tooltip title={tab.label} arrow>
								{tabIcon}
							</Tooltip>
						),
						label: showLabels ? tab.label : undefined,
						iconPosition: "start" as const,
						"aria-label": tab.label,
						"data-component": `tab-${tab.label.toLowerCase()}`,
					};

					return buildTabHref ? (
						<LinkTab
							key={index}
							{...commonProps}
							href={buildTabHref(slug)}
						/>
					) : (
						<Tab key={index} {...commonProps} />
					);
				})}
			</Tabs>
			<Box
				sx={{
					flexGrow: 1,
					minHeight: 0,
					overflowY: "auto",
					// Reserve scrollbar space to prevent width shift when content expands
					scrollbarGutter: "stable",
				}}
				data-component="tab-content-scroll"
			>
				{tabs.map((tab, index) => (
					<TabPanel key={index} value={currentTabIndex} index={index}>
						{tab.content}
					</TabPanel>
				))}
			</Box>

			{/* Delete confirmation dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
				data-component="delete-person-dialog"
			>
				<DialogTitle>Delete Person</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete{" "}
						<strong>{personInfo?.name ?? `Person #${personId}`}</strong>?
						This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeleteDialogOpen(false)}
						data-component="cancel-delete-button"
					>
						Cancel
					</Button>
					<Button
						onClick={handleDelete}
						color="error"
						variant="contained"
						data-component="confirm-delete-button"
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* Edit person dialog */}
			<Dialog
				open={editDialogOpen}
				onClose={() => setEditDialogOpen(false)}
				data-component="edit-person-dialog"
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Edit Person</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							label="Name"
							value={editName}
							onChange={(e) => setEditName(e.target.value)}
							fullWidth
							data-component="edit-name-field"
						/>
						<TextField
							label="Vanity Name"
							value={editVanityName}
							onChange={(e) => setEditVanityName(e.target.value)}
							fullWidth
							helperText="LinkedIn vanity URL slug (e.g., 'john-doe-123')"
							data-component="edit-vanity-name-field"
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setEditDialogOpen(false)}
						data-component="cancel-edit-button"
					>
						Cancel
					</Button>
					<Button
						onClick={handleSaveEdit}
						variant="contained"
						disabled={isEditing}
						startIcon={
							isEditing ? <CircularProgress size={16} /> : undefined
						}
						data-component="save-edit-button"
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
};
